(() => {
  const CART_MUTATION_PATH = /\/cart\/(add|change|update)(?:\.js)?$/;
  const CART_CHANGE_PATH = /\/cart\/change(?:\.js)?$/;
  const LOW_PRIORITY_HOSTS = [
    'anagram.ai',
    'posthog.com',
    'trekkie',
    'google-analytics',
    'googletagmanager',
    'facebook',
    'monorail',
  ];

  const normalizeCartErrorPayload = (payload) => {
    if (!payload) return '';

    if (typeof payload === 'string') return payload.trim();

    if (typeof payload.message === 'string') return payload.message.trim();
    if (payload.message && typeof payload.message === 'object') {
      return normalizeCartErrorPayload(payload.message);
    }

    if (typeof payload.description === 'string') return payload.description.trim();

    if (typeof payload.errors === 'string') return payload.errors.trim();
    if (payload.errors && typeof payload.errors === 'object') {
      return Object.values(payload.errors)
        .flat()
        .filter((value) => typeof value === 'string' && value.trim())
        .join(' ')
        .trim();
    }

    return '';
  };

  const getCartErrorToastElement = () => document.querySelector('cart-error-toast');

  const reportCartError = (message) => {
    const text = normalizeCartErrorPayload(message) || window.cartStrings?.error;
    if (!text) return;

    const toast = getCartErrorToastElement();
    if (toast && typeof toast.show === 'function') {
      toast.show(text);
      return;
    }

    publish(PUB_SUB_EVENTS.cartError, { source: 'fetch-interceptor', message: text });
  };

  const fetchUrlPath = (url) => {
    const raw = typeof url === 'string' ? url : url?.url ?? '';
    if (!raw) return '';
    try {
      return new URL(raw, window.location.origin).pathname;
    } catch (e) {
      return String(raw).split('?')[0];
    }
  };

  const isCartMutationUrl = (url) => CART_MUTATION_PATH.test(fetchUrlPath(url));
  const isCartChangeUrl = (url) => CART_CHANGE_PATH.test(fetchUrlPath(url));

  const isAbortLike = (error) => {
    const { name } = error || {};
    return name === 'AbortError' || name === 'TimeoutError';
  };

  const withFetchPriority = (rest, priority) => {
    const [init] = rest;
    if (init == null) {
      rest[0] = { priority };
      return;
    }
    if (typeof init === 'object' && !Array.isArray(init)) {
      rest[0] = { ...init, priority };
    }
  };

  const theme = (window.theme = window.theme || {});

  class CartErrorToast extends HTMLElement {
    connectedCallback() {
      this._messageEl = this.querySelector('[data-message]');
      this._closeBtn = this.querySelector('.cart-error-toast__close');
      this._onKeyDown = (event) => {
        if (event.key === 'Escape') this.hide();
      };

      this._closeBtn?.addEventListener('click', () => this.hide());

      subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        const text = normalizeCartErrorPayload(event);
        if (text) this.show(text);
      });

      this._interceptCartErrors();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
    }

    /**
     * Cart-mutation observer — not a global error monitor.
     *
     * This toast copy says the cart failed. The only fetches that may surface it
     * are POST /cart/add|update (and network throws on those). /cart/change is
     * owned by cart-drawer-items (stale-request guard); a second toast there
     * caused phantom limit messages after remove.
     *
     * Everything else is pass-through: GET /cart.js (503s intermittently),
     * section re-renders, GTM/pixels, Armada middleware. Treating those as cart
     * errors produced AAS-4273 (newsletter timeout) and AAS-5377 (mobile ATC
     * toast while the add itself succeeded).
     *
     * Installed once. Native fetch is stored on window.theme.nativeFetch so other
     * hooks (ski-set market guard) can read the cart without re-entering.
     */
    _interceptCartErrors() {
      if (theme.cartErrorInterceptorInstalled) return;
      theme.cartErrorInterceptorInstalled = true;

      const nativeFetch = window.fetch.bind(window);
      theme.nativeFetch = nativeFetch;

      window.fetch = (url, ...rest) => {
        const urlString = typeof url === 'string' ? url : url?.url ?? '';
        const mutation = isCartMutationUrl(url);
        const isLowPriority = LOW_PRIORITY_HOSTS.some((host) => urlString.includes(host));

        if (mutation) withFetchPriority(rest, 'high');
        else if (isLowPriority) withFetchPriority(rest, 'low');

        if (!mutation) return nativeFetch(url, ...rest);

        return nativeFetch(url, ...rest).then(
          (response) => {
            if (!response.ok && !isCartChangeUrl(url)) {
              response
                .clone()
                .json()
                .then((data) => {
                  reportCartError(normalizeCartErrorPayload(data) || window.cartStrings?.error);
                })
                .catch(() => {
                  reportCartError(window.cartStrings?.error);
                });
            }
            return response;
          },
          (networkError) => {
            if (!isAbortLike(networkError)) {
              reportCartError(window.cartStrings?.error);
            }
            throw networkError;
          }
        );
      };
    }

    show(message) {
      const text = normalizeCartErrorPayload(message) || window.cartStrings?.error;
      if (!text || !this._messageEl) return;

      const now = Date.now();
      if (text === this._lastShownText && now - (this._lastShownAt || 0) < 500) return;
      this._lastShownText = text;
      this._lastShownAt = now;

      this._messageEl.textContent = text;
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      this.offsetHeight; // eslint-disable-line no-unused-expressions
      this.classList.add('is-visible');

      document.addEventListener('keydown', this._onKeyDown);
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.hide(), CART_ERROR_TOAST_DURATION);
    }

    hide() {
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this._onKeyDown);
      clearTimeout(this._timer);

      this.addEventListener(
        'transitionend',
        () => this.setAttribute('hidden', ''),
        { once: true }
      );
    }
  }

  customElements.define('cart-error-toast', CartErrorToast);
})();
